'use client';
import { useEffect, useState, useCallback, memo } from 'react';
import { useRouter, usePathname, useParams } from 'next/navigation';
import { PageResponse } from '@commercetools/frontend-sdk/lib/types/api/page';
import { Category } from 'shared/types/product';
import { Params, SearchParams } from 'types/next';
import Renderer from 'frontastic/renderer';

export interface RendererProps {
  data: PageResponse;
  params: Params;
  searchParams: SearchParams;
  gridClassName?: string;
  wrapperClassName?: string;
  currentHighlight?: string;
  categories: Category[];
}
// Separate loading spinner into a memoized component
const LoadingSpinner = memo(() => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="size-64 flex animate-spin items-center justify-center rounded-full border-4 border-gray-300 border-t-gray-400"></div>
  </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';

const Wrapper = ({ data: pageData, searchParams: propSearchParams, categories = [] }: RendererProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const pathname = usePathname();
  console.log(`*** pageData-slug: ${pathname} :: `, pageData);
  const params = useParams();
  const router = useRouter();

  const isInvalidSlug = Object.values(pageData?.data?.dataSources || {}).some((value: any) => {
    return value.notFound === true;
  });

  useEffect(() => {
    window.et_pagename = document.title || 'Genius TV';
  }, [pathname]);

  useEffect(() => {
    if (isInvalidSlug) {
      console.group('%c Redirecting to 404', 'background-color: wheat; padding: 4px; color: black');
      console.log('--- invalid data from server ---');
      console.log('likely causes: slug not mapped to any cms content');
      console.groupEnd();
      router.push('/404/');
    }
  }, [isInvalidSlug]);

  // Override router methods
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const originalPush = router.push;
      const originalReplace = router.replace;
      const originalBack = router.back;
      const originalForward = router.forward;

      // Override push method
      router.push = function (...args: Parameters<typeof originalPush>) {
        setIsLoading(true);
        return originalPush.apply(this, args);
      };

      // Override replace method
      router.replace = function (...args: Parameters<typeof originalReplace>) {
        setIsLoading(true);
        return originalReplace.apply(this, args);
      };

      // Override back method
      router.back = function () {
        setIsLoading(true);
        return originalBack.apply(this);
      };

      // Override forward method
      router.forward = function () {
        setIsLoading(true);
        return originalForward.apply(this);
      };

      // Cleanup function to restore original methods
      return () => {
        router.push = originalPush;
        router.replace = originalReplace;
        router.back = originalBack;
        router.forward = originalForward;
      };
    }
  }, [router]);

  // Reset loading state on route change
  useEffect(() => {
    void pathname;
    setIsLoading(false);
  }, [pathname]);

  useEffect(() => {
    if (pageData) {
      setIsLoading(false); // Set loading to false once data is available
    }
  }, [pageData]);

  // Handle regular link clicks
  const handleGlobalClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (!target.closest('a')) return;

      const link = target.closest('a');
      const href = link?.getAttribute('href');

      if (
        !href ||
        !link ||
        !href.startsWith('/') ||
        href.startsWith('//') ||
        link.hasAttribute('download') ||
        link.target === '_blank' ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey
      )
        return;

      e.preventDefault();
      setIsLoading(true);
      router.push(href);
    },
    [router],
  );

  useEffect(() => {
    document.addEventListener('click', handleGlobalClick, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [handleGlobalClick]);

  return (
    <>
      {isLoading && false && <LoadingSpinner />}
      <Renderer data={pageData} params={params} searchParams={propSearchParams} categories={categories} />
    </>
  );
};

export default Wrapper;
